/**
 * Detects non-actionable `TypeError: Failed to fetch` (or `Load failed`)
 * errors that originate from a browser extension intercepting fetch calls.
 *
 * When an extension like an ad-blocker or privacy tool monkey-patches the
 * global `fetch`, transient network failures surface as TypeErrors whose
 * stack contains both extension frames (the initiator) and vendor-SDK
 * frames (e.g. Datadog browser-core / session-replay recorder). No
 * application source code is involved, so these errors are not actionable.
 *
 * Signature:
 * - kind/type is `TypeError`
 * - message is a generic fetch network error
 * - stack contains at least one browser-extension frame
 * - non-extension frames do NOT reference application source code
 */

const EXTENSION_SCHEMES = [
  "chrome-extension://",
  "moz-extension://",
  "safari-web-extension://",
];

const GENERIC_FETCH_ERROR_MESSAGES = [
  "failed to fetch",
  "load failed",
  "network error",
];

export function isBrowserExtensionFetchNoise(
  message: string | undefined,
  stack: string | undefined,
  kind: string | undefined,
): boolean {
  if (!message || !stack) {
    return false;
  }

  if (kind !== "TypeError" && !message.startsWith("TypeError")) {
    return false;
  }

  const normalizedMessage = message.toLowerCase().replace("typeerror: ", "");
  const isGenericFetchError = GENERIC_FETCH_ERROR_MESSAGES.some(
    (msg) => normalizedMessage === msg || normalizedMessage.endsWith(msg),
  );

  if (!isGenericFetchError) {
    return false;
  }

  const frames = stack
    .split("\n")
    .filter((line) => line.trim().startsWith("at "));

  if (frames.length === 0) {
    return false;
  }

  const hasExtensionFrame = frames.some((frame) =>
    EXTENSION_SCHEMES.some((scheme) => frame.includes(scheme)),
  );

  if (!hasExtensionFrame) {
    return false;
  }

  const nonExtensionFrames = frames.filter(
    (frame) => !EXTENSION_SCHEMES.some((scheme) => frame.includes(scheme)),
  );

  return nonExtensionFrames.every((frame) => !_isApplicationSourceFrame(frame));
}

function _isApplicationSourceFrame(frame: string): boolean {
  return (
    frame.includes("/src/") ||
    frame.includes("/app/") ||
    frame.includes("/lib/") ||
    frame.includes("/components/") ||
    frame.includes("webpack://_N_E/./src/")
  );
}
